import {
  Backdrop,
  Box,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { tokens } from '../../theme';
import { Button } from '../../components/Button';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCpfCnpj } from '../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCookiesLogin } from '../../config/cookies';
import { HOST_API } from '../../config/hosts';
import { greenColor } from '../../config/colors';
import ModalConsultResult from './ModalConsultResult';
import ModalPendingConsult from '../../components/ModalPendingConsult';

const validationSchema = Yup.object({
  taxvar: Yup.string()
    .transform((v) => v.replace(/\D/g, ''))
    .test('cpf-or-cnpj', 'CPF ou CNPJ inválido', (v) => v?.length === 11 || v?.length === 14)
    .required('Campo obrigatório'),
});

const FieldLabel = ({ label, colors }) => (
  <Typography variant="caption" sx={{ color: colors.grey[200], mb: '4px', display: 'block' }}>
    {label}
  </Typography>
);

const ModalFirstConsult = ({ open, onClose, onConsult }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { cookies } = useCookiesLogin();
  
  const [loadingMessage, setLoadingMessage] = useState('');
  const [dataAPI, setDataAPI] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [pendingTaxvar, setPendingTaxvar] = useState('');

  const formik = useFormik({
    initialValues: { taxvar: '' },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      const taxvar = values.taxvar.replace(/\D/g, '');

      setLoadingMessage(t('onboarding.firstConsult.loading.searching'));

      const slowTimer = setTimeout(() => {
        setLoadingMessage(t('onboarding.firstConsult.loading.processing'));
      }, 5000);

      const verySlowTimer = setTimeout(() => {
        setLoadingMessage(t('onboarding.firstConsult.loading.finalizing'));
      }, 15000);

      try {
        const { token } = cookies.AUTH_TOKEN;
        const response = await fetch(`${HOST_API}/v1/order/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ taxvar }),
        });

        const result = await response.json();

        if (!response.ok || !result?.status) {
          console.error('Erro na consulta');
          return;
        }

        const stage = result.data?.stage;

        if (result.fromCache && (stage === 'PENDING' || stage === 'CREATED')) {
          setPendingOrderData(result.data);
          setPendingTaxvar(taxvar);
          setShowPendingModal(true);
          return;
        }


        setDataAPI(result.data);
        setResultOpen(true);
        onConsult?.(taxvar);
      } finally {
        clearTimeout(slowTimer);
        clearTimeout(verySlowTimer);
        setLoadingMessage('');
        setSubmitting(false);
      }
    },
  });

  const handleWait = () => {
    setShowPendingModal(false);
    setPendingOrderData(null);
    setPendingTaxvar('');
  };

  const handleNewConsult = () => {
    setShowPendingModal(false);
    setPendingOrderData(null);
    setPendingTaxvar('');
    formik.resetForm();
  };

  const handleClose = () => {
    formik.resetForm();
    setDataAPI(null);
    onClose?.();
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setDataAPI(null);
    formik.resetForm();
    onClose?.();
  };

  const handleBuyCredits = () => {
    formik.resetForm();
    setDataAPI(null);
    onClose?.();
    setTimeout(() => navigate('/app/redscore'), 0);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' },
  };

  const fieldProps = (field) => ({
    size: 'small',
    fullWidth: true,
    error: formik.touched[field] && !!formik.errors[field],
    helperText: (formik.touched[field] && formik.errors[field]) ?? ' ',
    onBlur: formik.handleBlur,
    name: field,
    sx: inputSx,
  });

  return (
    <>
      <Backdrop open={!!loadingMessage} sx={{ zIndex: 9999, flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: greenColor }} />
        <Typography sx={{ color: '#fff', fontSize: '14px', mt: 1 }}>
          {loadingMessage}
        </Typography>
      </Backdrop>

      <ModalConsultResult open={resultOpen} onClose={handleResultClose} dataAPI={dataAPI} />

      <ModalPendingConsult
        open={showPendingModal}
        onWait={handleWait}
        onNewConsult={handleNewConsult}
      />


      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            border: `2px solid ${colors.greenAccent[500]}`,
            p: { xs: '32px 24px', sm: '48px 56px' },
            maxWidth: 560,
            width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            outline: 'none',
            animation: 'fadeSlideUp 0.35s ease',
            '@keyframes fadeSlideUp': {
              from: { opacity: 0, transform: 'translateY(24px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: `${colors.greenAccent[500]}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.greenAccent[400],
            }}
          >
            <CheckCircleIcon fontSize="large" />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight={500} sx={{ mb: '16px', color: colors.grey[100] }}>
              {t('onboarding.firstConsult.title')}
            </Typography>
            <Typography
              variant="body1"
              fontWeight={500}
              sx={{ color: colors.grey[100], mb: '8px' }}
            >
              {t('onboarding.firstConsult.subtitle')}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[400], lineHeight: 1.6 }}>
              {t('onboarding.firstConsult.description')}
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
              width: '100%',
              border: `1px solid ${colors.grey[700]}`,
              borderRadius: '10px',
              p: '20px 24px',
            }}
          >
            <Box sx={{ mb: '16px' }}>
              <FieldLabel label={t('forms.cpfCnpj')} colors={colors} />
              <TextField
                {...fieldProps('taxvar')}
                placeholder="000.000.000-00"
                value={formik.values.taxvar}
                onChange={(e) => formik.setFieldValue('taxvar', formatCpfCnpj(e.target.value))}
                inputProps={{ maxLength: 18 }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={formik.isSubmitting || !formik.values.taxvar}
              startIcon={<Search size={18} />}
              sx={{ fontWeight: 500, fontSize: '12px', py: '8px' }}
            >
              {t('onboarding.firstConsult.buttonLabel')}
            </Button>

            <Typography
              variant="caption"
              sx={{ color: colors.grey[500], mt: '12px', fontSize: '9px' }}
            >
              {t('onboarding.firstConsult.disclaimer')}
            </Typography>
          </Box>

          <Box
            component="span"
            onClick={handleBuyCredits}
            sx={{
              color: colors.greenAccent[500],
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.06em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textTransform: 'uppercase',
              '&:hover': { opacity: 0.8 },
            }}
          >
            {t('onboarding.firstConsult.buyCredits')} →
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ModalFirstConsult;
